// src/lib/api/moduleApi.ts
import { getApiClient } from './client';
import {
    Module,
    CourseStatus,
    Case,
    ModuleWithCases,
    ModuleStep,
    ModuleFullResponse,
    CandidateModuleProgress,
    SubmitStepResultRequest,
    StepResultResponse,
    IntroStatus,
    CreateModuleRequest,
    UpdateModuleRequest,
    CreateCaseRequest,
    UpdateCaseRequest,
    PaginationRequest
} from '../models/module';
import { ApiError, buildSearchParams } from 'jasit-api-kit';

// Module Management

const getModules = async (pagination?: PaginationRequest): Promise<Module[]> => {
    const params = pagination ? buildSearchParams({ ...pagination }) : undefined;
    return getApiClient().get<Module[]>('/api/modules', { params });
};

// Returns global modules + modules scoped to the authenticated user's organisation
const getMyModules = async (pagination?: PaginationRequest): Promise<Module[]> => {
    const params = pagination ? buildSearchParams({ ...pagination }) : undefined;
    return getApiClient().get<Module[]>('/api/my/modules', { params });
};

const getModuleById = async (moduleId: string): Promise<Module | null> => {
    try {
        return await getApiClient().get<Module>(`/api/modules/${moduleId}`);
    } catch (error) {
        if (error instanceof ApiError && error.statusCode === 404) {
            return null;
        }
        throw error;
    }
};

const getModuleWithCases = async (moduleId: string): Promise<ModuleWithCases | null> => {
    try {
        return await getApiClient().get<ModuleWithCases>(`/api/modules/${moduleId}/with-cases`);
    } catch (error) {
        if (error instanceof ApiError && error.statusCode === 404) {
            return null;
        }
        throw error;
    }
};

// ADMIN-ONLY as of #352, and unused by any component today.
//
// Employer course authoring does NOT go through these — it uses
// onboardingApi's /api/onboarding/modules, which is org-scoped server-side.
// These three were on an unauthenticated backend route until #352 moved them to
// the Admin group, so calling them as an employer or candidate now 403s.
//
// They are kept for #50 (Nexilink platform courses, authored by admins with no
// organisation_id). If you are reaching for one of these from an employer
// surface, you want onboardingApi instead.
const createModule = async (moduleData: CreateModuleRequest): Promise<Module> => {
    return getApiClient().post<Module>('/api/modules', moduleData);
};

const updateModule = async (moduleId: string, moduleData: UpdateModuleRequest): Promise<Module> => {
    return getApiClient().put<Module>(`/api/modules/${moduleId}`, moduleData);
};

const deleteModule = async (moduleId: string): Promise<void> => {
    return getApiClient().delete<void>(`/api/modules/${moduleId}`);
};

// #75 course lifecycle transition (publish / archive / re-open). The backend
// guards the move against the legal state graph and rejects cross-org changes.
const updateModuleStatus = async (moduleId: string, status: CourseStatus): Promise<Module> => {
    return getApiClient().patch<Module>(`/api/modules/${moduleId}/status`, { status });
};

// Case Management
//
// ADMIN-ONLY as of #352, and unused by any component. The case data a course
// actually needs at runtime comes from getCasesForModule / getModuleWithCases
// below, which hit the authenticated /api/modules/:id/... routes.

const getCases = async (pagination?: PaginationRequest): Promise<Case[]> => {
    const params = pagination ? buildSearchParams({ ...pagination }) : undefined;
    return getApiClient().get<Case[]>('/api/cases', { params });
};

const getCaseById = async (caseId: string): Promise<Case | null> => {
    try {
        return await getApiClient().get<Case>(`/api/cases/${caseId}`);
    } catch (error) {
        if (error instanceof ApiError && error.statusCode === 404) {
            return null;
        }
        throw error;
    }
};

const getCasesForModule = async (moduleId: string): Promise<Case[]> => {
    try {
        return await getApiClient().get<Case[]>(`/api/modules/${moduleId}/cases`);
    } catch (error) {
        if (error instanceof ApiError && error.statusCode === 404) {
            return [];
        }
        throw error;
    }
};

const createCase = async (caseData: CreateCaseRequest): Promise<Case> => {
    return getApiClient().post<Case>('/api/cases', caseData);
};

const updateCase = async (caseId: string, caseData: UpdateCaseRequest): Promise<Case> => {
    return getApiClient().put<Case>(`/api/cases/${caseId}`, caseData);
};

const deleteCase = async (caseId: string): Promise<void> => {
    return getApiClient().delete<void>(`/api/cases/${caseId}`);
};

// Step & Progress Management

const getModuleSteps = async (moduleId: string): Promise<ModuleStep[]> => {
    return getApiClient().get<ModuleStep[]>(`/api/modules/${moduleId}/steps`);
};

const getModuleFull = async (moduleId: string): Promise<ModuleFullResponse | null> => {
    try {
        return await getApiClient().get<ModuleFullResponse>(`/api/modules/${moduleId}/full`);
    } catch (error) {
        if (error instanceof ApiError && error.statusCode === 404) {
            return null;
        }
        throw error;
    }
};

const getModuleProgress = async (moduleId: string): Promise<CandidateModuleProgress | null> => {
    try {
        return await getApiClient().get<CandidateModuleProgress>(`/api/modules/${moduleId}/progress`);
    } catch (error) {
        if (error instanceof ApiError && error.statusCode === 404) {
            return null;
        }
        throw error;
    }
};

const getMyProgress = async (): Promise<CandidateModuleProgress[]> => {
    return getApiClient().get<CandidateModuleProgress[]>('/api/my/progress');
};

const submitStepResult = async (moduleId: string, request: SubmitStepResultRequest): Promise<StepResultResponse> => {
    return getApiClient().post<StepResultResponse>(`/api/modules/${moduleId}/progress/submit`, request);
};

const getIntroStatus = async (): Promise<IntroStatus> => {
    return getApiClient().get<IntroStatus>('/api/my/intro-status');
};

// ===================================
// #293 — reading back free-text course answers (employer-facing)
// ===================================

/** One free-text question and what the candidate wrote for it. */
export interface OpenAnswer {
    module_id: string;
    module_name: string;
    step_order: number;
    step_title: string;
    step_type: string;
    question_text: string;
    /** Empty when the candidate has not reached the step yet. */
    answer_text: string;
    answered: boolean;
    submitted_at?: string;
}

interface OpenAnswersResponse {
    answers: OpenAnswer[];
    total: number;
}

// Every open answer this EMPLOYEE has written, across the modules they have
// started. Backend gates it on an active engagement in the caller's org.
const getEmployeeOpenAnswers = async (userId: string): Promise<OpenAnswer[]> => {
    const res = await getApiClient().get<OpenAnswersResponse>(`/api/employees/${userId}/open-answers`);
    return res?.answers ?? [];
};

// The open answers an APPLICANT wrote in the modules THIS job requires — read
// before the hire decision. Backend gates it on owning the need, and scopes it
// to that need's required modules so an employer cannot read a candidate's
// writing from some other organisation's course.
const getApplicantOpenAnswers = async (applicationId: string): Promise<OpenAnswer[]> => {
    const res = await getApiClient().get<OpenAnswersResponse>(`/api/applications/${applicationId}/open-answers`);
    return res?.answers ?? [];
};

export const moduleApi = {
    // Module operations
    getModules,
    getMyModules,
    getModuleById,
    getModuleWithCases,
    createModule,
    updateModule,
    updateModuleStatus,
    deleteModule,

    // Case operations
    getCases,
    getCaseById,
    getCasesForModule,
    createCase,
    updateCase,
    deleteCase,

    // Step & Progress operations
    getModuleSteps,
    getModuleFull,
    getModuleProgress,
    getMyProgress,
    submitStepResult,
    getIntroStatus,

    // Free-text answers (#293)
    getEmployeeOpenAnswers,
    getApplicantOpenAnswers,
};
